<script setup>
import { computed } from 'vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [{ value: '', label: '' }]
  },
  modelValue: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const isActive = computed(() => {
  return props.modelValue === props.data[1].value;
});

const emit = defineEmits(['update:modelValue']);
const toggleVal = () => {
  if (props.disabled) return;
  const newValue = isActive.value ? props.data[0].value : props.data[1].value;
  emit('update:modelValue', newValue);
};
</script>

<template>
  <div
    class="v-switch"
    :class="{ disabled }"
    :tabindex="disabled ? -1 : 0"
    @click="toggleVal"
    @keypress.enter="toggleVal"
    @keypress.space="toggleVal"
  >
    <label>
      <component v-if="typeof data[0].label === 'object'" :is="data[0].label" />
      <span v-else>{{ data[0].label }}</span>
    </label>
    <span
      class="v-switch__slider"
      :class="{ 'v-switch__slider--active': isActive }"
    >
      <span
        class="v-switch__handle"
        :class="{
          'v-switch__handle--active': isActive,
          'v-switch__handle--inactive': !isActive
        }"
      ></span>
    </span>
    <label>
      <component v-if="typeof data[1].label === 'object'" :is="data[1].label" />
      <span v-else>{{ data[1].label }}</span>
    </label>
  </div>
</template>

<style lang="scss" scoped>
.v-switch {
  $handleSize: 16px;
  display: flex;
  align-items: center;
  gap: var(--spacing-xsmall);
  cursor: pointer;
  width: fit-content;
  font-size: var(--type-small-body);

  label {
    svg {
      cursor: pointer;
      display: flex;
      width: 18px;
    }
  }

  &__slider {
    width: 42px;
    height: 24px;
    border-radius: 100px;
    border: var(--border-stroke-default) var(--color-border-strong) solid;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    padding: var(--spacing-xsmall);
    position: relative;

    &--active {
      background-color: var(--color-primary-bg-default);
      border: none;
    }
  }
  &__handle {
    display: inline-block;
    position: absolute;
    height: $handleSize;
    min-width: $handleSize;
    border-radius: var(--radius-full);
    background-color: white;
    transition: all 0.2s ease;
    &--active {
      animation: active-on 0.2s forwards;
    }
    &--inactive {
      animation: active-off 0.2s forwards;
    }
  }

  &:hover {
    .v-switch__slider {
      box-shadow: 0 0 0 var(--border-stroke-default) var(--color-border-strong);
    }
    color: var(--color-text-hover);
  }

  &:focus-visible {
    .v-switch__slider {
      box-shadow: 0 0 0 var(--border-stroke-strong)
        var(--color-secondary-border-pressed);
      border-color: var(--color-secondary-border-pressed);
    }
  }

  &.disabled {
    pointer-events: none;
    color: var(--color-text-disabled);
    .input {
      border-color: var(--color-border-disabled);
      .marker {
        background-color: transparent;
      }
    }
    svg * {
      fill: var(--color-icon-disabled);
    }
  }

  @keyframes active-on {
    0% {
      left: var(--spacing-xsmall);
      right: calc(100% - var(--spacing-xsmall) - $handleSize);
    }
    50% {
      left: var(--spacing-xsmall);
      right: var(--spacing-xsmall);
    }
    100% {
      left: calc(100% - var(--spacing-xsmall) - $handleSize);
      right: var(--spacing-xsmall);
    }
  }
  @keyframes active-off {
    0% {
      left: calc(100% - var(--spacing-xsmall) - $handleSize);
      right: var(--spacing-xsmall);
    }
    50% {
      left: var(--spacing-xsmall);
      right: var(--spacing-xsmall);
    }
    100% {
      left: var(--spacing-xsmall);
      right: calc(100% - var(--spacing-xsmall) - $handleSize);
    }
  }
}
</style>
